import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import { getStripeProduct } from '@cartwhl/common/api/getStripeProduct';
import { StripePrice, StripeProduct } from '@cartwhl/common/types';
import { getStripeProductID } from '@cartwhl/common/utils/getStripeProductID';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';

export const SPINS_PER_PURCHASE = 150;

export type ProductStoreState = {
  products: {
    free: StripeProduct | null;
    pro: StripeProduct | null;
    unlimited: StripeProduct | null;
    more: StripeProduct | null;
  };
  prices: StripePrice[];
  loading: boolean;
  done: boolean;
};

export const state = proxy<ProductStoreState>({
  loading: false,
  products: {
    free: null,
    pro: null,
    unlimited: null,
    more: null,
  },
  prices: [],
  done: false,
});

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'Product', enabled: true });
}

export async function fetchProducts() {
  if (state.loading || state.done) return;

  state.loading = true;

  const freePromise = fetchProduct(getStripeProductID('free'));
  const proPromise = fetchProduct(getStripeProductID('pro'));
  const unlimitedPromise = fetchProduct(getStripeProductID('advanced'));
  const morePromise = fetchProduct(getStripeProductID('more'));

  const [free, pro, unlimited, more] = await Promise.all([
    freePromise,
    proPromise,
    unlimitedPromise,
    morePromise,
  ]);

  if (free !== null) {
    state.products.free = free.product;
    state.prices = state.prices.concat(free.prices);
  }
  if (pro !== null) {
    state.products.pro = pro.product;
    state.prices = state.prices.concat(pro.prices);
  }
  if (more !== null) {
    state.products.more = more.product;
    state.prices = state.prices.concat(more.prices);
  }
  if (unlimited !== null) {
    state.products.unlimited = unlimited.product;
    state.prices = state.prices.concat(unlimited.prices);
  }

  state.done = true;
  state.loading = false;
}

export async function fetchProduct(stripeProductID: string) {
  const { data, error } = await getStripeProduct(stripeProductID);
  if (error) {
    await handleGenericErrors(error);
  } else if (data) {
    return data;
  }
  return null;
}

export function getProductByID(stripeProductID: string) {
  const products = Object.values(state.products);
  for (let i = 0; i < products.length; i++) {
    const product = products[i];
    if (product && product.id === stripeProductID) {
      return product;
    }
  }
  return null;
}

export function getPriceByID(stripePriceID: string) {
  const prices = Object.values(state.prices);
  for (let i = 0; i < prices.length; i++) {
    const price = prices[i];
    if (price && price.id === stripePriceID) {
      return price;
    }
  }
  return null;
}

export function getDefaultPriceByProduct(
  product: StripeProduct | null,
): StripePrice | null {
  if (product === null) {
    return null;
  }
  for (let i = 0; i < state.prices.length; i++) {
    const price = state.prices[i];
    if (price.id === product.default_price) {
      return price;
    }
  }
  return null;
}

export function getPriceByProductAndMode(
  product?: string,
  mode: 'onetime' | 'month' | 'year' = 'month',
): StripePrice | null {
  if (typeof product !== 'string') {
    return null;
  }

  // First try to find the default price that matches the mode
  const productObj = getProductByID(product);
  if (productObj && productObj.default_price) {
    const defaultPrice = getPriceByID(productObj.default_price);
    if (defaultPrice) {
      const interval =
        (defaultPrice.recurring && defaultPrice.recurring.interval) ||
        'onetime';
      if (interval === mode) {
        return defaultPrice;
      }
    }
  }

  // If no default price matches, fall back to the first matching price
  for (let i = 0; i < state.prices.length; i++) {
    const price = state.prices[i];
    const interval = (price.recurring && price.recurring.interval) || 'onetime';
    if (price.product === product && interval === mode) {
      return price;
    }
  }

  return null;
}

export function getSavings(
  type: 'pro' | 'free' | 'more' | 'unlimited',
): number | null {
  const productID = getStripeProductID(type);
  const monthlyProPriceAsYear = getPriceByProductAndMode(productID, 'month');
  const yearlyProPrice = getPriceByProductAndMode(productID, 'year');
  let savings = null;
  if (monthlyProPriceAsYear && yearlyProPrice) {
    savings = Math.floor(
      100 *
      (1 -
        yearlyProPrice.unit_amount /
        (12 * monthlyProPriceAsYear.unit_amount)),
    );
  }
  return savings;
}

export function formatMoney(v: number) {
  return v / 100;
}

export function formatInterval(type: string) {
  switch (type) {
    case 'month':
      return 'mo';
    case 'year':
      return 'yr';
    default:
      return 'order';
  }
}

export function formatDigits(v: number, length: number) {
  let str = v + '';
  while (str.length < length) {
    str = `0${str}`;
  }
  return str;
}
